import { useCallback } from 'react';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { PlaylistItemResolved } from 'src/features/AddToPlaylist/api';
import {
  playUrlForPlaylistItem,
  playlistContentTypeLabel,
  withPlaylistQueryParams,
} from 'src/features/AddToPlaylist/playlistPaths';
import { Link } from 'react-router-dom';
import { usePlaylistMutations } from 'src/features/AddToPlaylist';

export function PlaylistItemsList({
  playlistId,
  items,
}: {
  playlistId: string;
  items: PlaylistItemResolved[];
}) {
  const { removeItem, reorderItems } = usePlaylistMutations();

  const sorted = [...items].sort((a, b) => a.position - b.position);
  const orderedIds = sorted.map((i) => i.id);

  const onDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination || result.destination.index === result.source.index)
        return;
      const next = [...orderedIds];
      const [r] = next.splice(result.source.index, 1);
      if (r === undefined) return;
      next.splice(result.destination.index, 0, r);
      void reorderItems.mutateAsync({ playlistId, itemIds: next }).catch(() => {});
    },
    [orderedIds, playlistId, reorderItems],
  );

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId={`pl-${playlistId}`}>
        {(prov) => (
          <List ref={prov.innerRef} {...prov.droppableProps}>
            {sorted.map((it, idx) => (
              <Draggable key={it.id} draggableId={it.id} index={idx}>
                {(dp) => (
                  <ListItem
                    ref={dp.innerRef}
                    {...dp.draggableProps}
                    divider
                    secondaryAction={
                      <IconButton edge="end" aria-label="remove" size="small" onClick={() => {
                        void removeItem.mutateAsync({
                          playlistId,
                          itemId: it.id,
                        }).catch(() => {});
                      }}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    <span {...dp.dragHandleProps} style={{ cursor: 'grab', marginRight: 8 }}>
                      <DragIndicatorIcon fontSize="small" />
                    </span>
                    {it.thumbnailUrl ? (
                      <img src={it.thumbnailUrl} alt="" style={{ width: 72, height: 40, objectFit: 'cover', marginRight: 12 }} />
                    ) : null}
                    <ListItemText
                      primary={
                        <Link
                          to={withPlaylistQueryParams(playUrlForPlaylistItem(it), playlistId, it.id)}
                          style={{ color: 'inherit' }}
                        >
                          {it.title}
                        </Link>
                      }
                      secondary={playlistContentTypeLabel(it.contentType)}
                    />
                  </ListItem>
                )}
              </Draggable>
            ))}
            {prov.placeholder}
          </List>
        )}
      </Droppable>
    </DragDropContext>
  );
}
